import type { Metadata } from "next"
import Link from "next/link"
import { ArrowLeft, ArrowRight } from "lucide-react"

import { Container } from "@/components/layout/container"
import { VinegarConverter } from "@/components/calculators/vinegar-converter"

export const metadata: Metadata = {
  title: "Конвертер оцту для домашніх заготовок",
  description:
    "Конвертер оцту для домашніх заготовок: перерахунок між 3%, 6% і 9% оцтом для точних пропорцій у рецептах.",
  alternates: {
    canonical: "/calculators/vinegar-converter",
  },
  openGraph: {
    title: "Конвертер оцту для домашніх заготовок",
    description:
      "Перерахунок між 3%, 6% і 9% оцтом для точних пропорцій у рецептах домашньої консервації.",
    url: "/calculators/vinegar-converter",
    type: "website",
  },
  twitter: {
    card: "summary",
    title: "Конвертер оцту для домашніх заготовок",
    description:
      "Швидкий перерахунок концентрації оцту для рецептів домашньої консервації.",
  },
}

const nextLinks = [
  {
    title: "Усі калькулятори",
    href: "/calculators",
  },
  {
    title: "Каталог рецептів",
    href: "/recipes",
  },
  {
    title: "Статті і пояснення",
    href: "/blog",
  },
]

export default function VinegarConverterPage() {
  return (
    <>
      <section className="border-b border-neutral-200 bg-neutral-50/70 py-5">
        <Container>
          <Link
            href="/calculators"
            className="inline-flex items-center text-sm font-medium text-neutral-700 transition-colors hover:text-neutral-950"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            До всіх калькуляторів
          </Link>
        </Container>
      </section>

      <section className="border-b border-neutral-200 bg-white py-12 sm:py-16">
        <Container className="max-w-4xl">
          <div className="eyebrow">Конвертер оцту</div>

          <h1 className="mt-4 text-4xl font-semibold leading-tight text-neutral-950 sm:text-5xl">
            Перерахунок концентрації оцту для рецептів
          </h1>

          <p className="mt-5 text-base leading-8 text-neutral-700 sm:text-lg">
            Якщо у рецепті вказана одна концентрація оцту, а у вас під рукою
            інша, цей інструмент допоможе швидко зорієнтуватися. Конвертер
            особливо корисний для домашньої консервації, де точність впливає і
            на смак, і на результат зберігання.
          </p>
        </Container>
      </section>

      <section className="py-10 sm:py-12">
        <Container>
          <VinegarConverter />
        </Container>
      </section>

      <section className="border-t border-neutral-200 bg-neutral-50 py-12 sm:py-14">
        <Container>
          <div className="rounded-[2rem] border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-6 max-w-3xl">
              <h2 className="text-2xl font-semibold text-neutral-950 sm:text-3xl">
                Що може знадобитися далі
              </h2>

              <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
                Після перерахунку концентрації оцту користувачі часто переходять
                до конкретного рецепта, іншого калькулятора або до статті з
                поясненнями щодо пропорцій і безпеки.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {nextLinks.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="rounded-[1.5rem] border border-neutral-200 bg-neutral-50 p-5 transition-colors hover:bg-emerald-50"
                >
                  <div className="text-base font-semibold text-neutral-950">
                    {item.title}
                  </div>

                  <div className="mt-4 inline-flex items-center text-sm font-semibold text-emerald-700">
                    Перейти
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </Container>
      </section>
    </>
  )
}